import ScButton from "@/src/components/ui-kit/button";
import {
  ADMIN_ROLE,
  AVAILABLE_ROLES,
  MEMBER_ROLE,
} from "@/src/constants/roles-permissions.constants";
import type {
  BlocksPermission,
  UdsPolicyMapEntry,
  UdsPolicyRole,
} from "@/src/models/permissions.model";
import { updateUdsPolicyForPermission } from "@/src/services/uam/gateway/permissions.gateway";
import { useTheme } from "@/src/styles/theme-provider";
import {
  useAllBlocksPermissionsQuery,
  useReloadUdsConfigurationsMutation,
  useRoleBlocksPermissionsQuery,
  useUdsPolicyMapQuery,
  useUpdateRolePermissionsMutation,
} from "@/src/services/uam/permissions.blocks.api";
import {
  ArrowRight2,
  CloseCircle,
  User,
  UserOctagon,
} from "iconsax-react-nativejs";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Modal,
  Pressable,
  ScrollView,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import Toast from "react-native-toast-message";
import ConfirmModal from "../../action-modals/confirm-modal";
import ScAccordion from "../../ui-kit/accordion";
import ScToggle from "../../ui-kit/toggle";
import PermissionItem from "./permission-item";

export type AvailableRole = (typeof AVAILABLE_ROLES)[number];

type RolesPermissionsLoadingProps = Readonly<{ label: string }>;
const RolesPermissionsLoading = ({ label }: RolesPermissionsLoadingProps) => (
  <View className="flex-1 justify-center items-center p-4">
    <Text className="text-slate text-sm">{label}</Text>
  </View>
);

type RolesPermissionsErrorProps = Readonly<{ label: string }>;
const RolesPermissionsError = ({ label }: RolesPermissionsErrorProps) => (
  <View className="flex-1 justify-center items-center p-4">
    <Text className="text-crimson text-sm">{label}</Text>
  </View>
);

type RoleCardProps = Readonly<{
  role: AvailableRole;
  isSelected: boolean;
  permissionCount: number;
  onPress: (role: AvailableRole) => void;
  adminDescription: string;
  memberDescription: string;
  permissionsLabel: string;
  isMobile?: boolean;
}>;
const RoleCard = ({
  role,
  isSelected,
  permissionCount,
  onPress,
  adminDescription,
  memberDescription,
  permissionsLabel,
  isMobile,
}: RoleCardProps) => {
  const { themeVars } = useTheme();
  const isAdmin = role === ADMIN_ROLE;

  const roleIcon = isAdmin ? (
    <UserOctagon size={22} color={themeVars["--color-ash-2"] as string} />
  ) : (
    <User size={20} color={themeVars["--color-ash-2"] as string} />
  );

  // Mobile: a borderless tappable row that opens the permissions sheet,
  // so you don't have to scroll past the roles to reach the permissions.
  if (isMobile) {
    return (
      <Pressable
        className="flex-row items-center gap-3 bg-chalk rounded-2xl px-4 py-4 active:opacity-80 hover:bg-ash-3"
        onPress={() => onPress(role)}
      >
        <View className="bg-ash rounded-xl p-2.5">{roleIcon}</View>
        <View className="flex-1">
          <Text className="font-medium text-onyx capitalize">{role}</Text>
          <Text className="text-slate text-xs" numberOfLines={1}>
            {isAdmin ? adminDescription : memberDescription}
          </Text>
        </View>
        <ArrowRight2
          size={18}
          color={themeVars["--color-slate-default"] as string}
        />
      </Pressable>
    );
  }

  return (
    <Pressable
      className={`border px-6 py-4 gap-4 rounded-[16px] active:opacity-90 web:hover:border-ash-1 ${
        isSelected
          ? "border-primary bg-primary-2 hover:bg-primary-2"
          : "border-ash-3 bg-chalk hover:bg-ash-3"
      }`}
      onPress={() => onPress(role)}
    >
      <View className="flex-row gap-4 items-center">
        {roleIcon}
        <Text className="font-medium text-onyx">{role}</Text>
      </View>
      <Text className="hidden sm:flex text-slate text-sm">
        {isAdmin ? adminDescription : memberDescription}
      </Text>
      {isSelected && (
        <View className="hidden sm:flex flex-col sm:flex-row justify-between">
          <Text className="text-slate text-sm">
            {permissionCount} {permissionsLabel}
          </Text>
        </View>
      )}
    </Pressable>
  );
};

const RolesAndPermission = () => {
  const { t } = useTranslation();
  const { themeVars, varStyle } = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  const isMobile = windowWidth < 768;

  const [selectedRole, setSelectedRole] = useState<AvailableRole>("member");
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
  const [localPermissionIds, setLocalPermissionIds] = useState(
    new Set<string>(),
  );
  const [originalPermissionIds, setOriginalPermissionIds] = useState(
    new Set<string>(),
  );
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const {
    data: allPermissionsData,
    isLoading: isAllPermissionsLoading,
    isError: isAllPermissionsError,
  } = useAllBlocksPermissionsQuery();
  const { mutate: reloadUdsConfigurations } =
    useReloadUdsConfigurationsMutation();

  const {
    data: rolePermissionsData,
    isLoading: isRolePermissionsLoading,
    isError: isRolePermissionsError,
  } = useRoleBlocksPermissionsQuery(selectedRole);

  const { mutate: updateRolePermissions, isPending: isUpdating } =
    useUpdateRolePermissionsMutation();

  const { data: udsPolicyMap = {} } = useUdsPolicyMapQuery();

  const isPermissionsLoading =
    isAllPermissionsLoading || isRolePermissionsLoading;
  const isPermissionsError = isAllPermissionsError || isRolePermissionsError;

  const allPermissions = allPermissionsData?.data ?? [];
  const rolePermissionCount = rolePermissionsData?.totalCount ?? 0;

  const hasChanges =
    JSON.stringify([...localPermissionIds].sort()) !==
    JSON.stringify([...originalPermissionIds].sort());

  const groupedByResourceGroup = useMemo(
    () =>
      allPermissions.reduce(
        (
          groups: Readonly<Record<string, ReadonlyArray<BlocksPermission>>>,
          permission,
        ) => {
          const group = permission?.resourceGroup;
          return {
            ...groups,
            [group]: [...(groups[group] ?? []), permission],
          };
        },
        {} as Readonly<Record<string, ReadonlyArray<BlocksPermission>>>,
      ),
    [allPermissions],
  );

  useEffect(() => {
    const roleIds = new Set(
      rolePermissionsData?.data?.map((permission) => permission?.itemId) ?? [],
    );
    setLocalPermissionIds(roleIds);
    setOriginalPermissionIds(roleIds);
  }, [rolePermissionsData]);

  const syncAllChangedUdsPolicies = (
    changedRole: UdsPolicyRole,
    addPermissions: ReadonlyArray<string>,
    removePermissions: ReadonlyArray<string>,
  ): Promise<void> => {
    const affectedPermissionIds = [
      ...new Set([...addPermissions, ...removePermissions]),
    ].filter((permissionId) => permissionId in udsPolicyMap);

    return Promise.all(
      affectedPermissionIds.map((permissionId) => {
        const entry = udsPolicyMap[permissionId] as UdsPolicyMapEntry;

        const adminEnabled =
          changedRole === ADMIN_ROLE
            ? addPermissions.includes(permissionId)
            : entry.adminEnabled;

        const memberEnabled =
          changedRole === MEMBER_ROLE
            ? addPermissions.includes(permissionId)
            : entry.memberEnabled;

        return updateUdsPolicyForPermission(
          {
            permissionItemId: permissionId,
            resourceGroup: allPermissions.find((p) => p.itemId === permissionId)
              ?.resourceGroup,
            adminEnabled,
            memberEnabled,
          },
          udsPolicyMap,
        );
      }),
    ).then(() => void 0);
  };

  const handleTogglePermission = (permissionId: string, value: boolean) => {
    setLocalPermissionIds((prev) => {
      const updatedIds = new Set(prev);
      value ? updatedIds.add(permissionId) : updatedIds.delete(permissionId);
      return updatedIds;
    });
  };

  const handleToggleGroup = (
    groupPermissions: ReadonlyArray<BlocksPermission>,
    value: boolean,
  ) => {
    setLocalPermissionIds((prev) => {
      const updatedIds = new Set(prev);
      groupPermissions.forEach((permission) =>
        value
          ? updatedIds.add(permission?.itemId)
          : updatedIds.delete(permission?.itemId),
      );
      return updatedIds;
    });
  };

  const handleSave = () => setShowConfirmModal(true);

  const handleConfirmSave = async () => {
    const addPermissions = [...localPermissionIds].filter(
      (permissionId) => !originalPermissionIds.has(permissionId),
    );
    const removePermissions = [...originalPermissionIds].filter(
      (permissionId) => !localPermissionIds.has(permissionId),
    );

    if (selectedRole === ADMIN_ROLE || selectedRole === MEMBER_ROLE) {
      try {
        await syncAllChangedUdsPolicies(
          selectedRole,
          addPermissions,
          removePermissions,
        );
      } catch {
        Toast.show({
          type: "error",
          text1: t("ERROR_ROLE_PERMISSIONS_UPDATED"),
        });
        return;
      }
    }

    updateRolePermissions(
      { slug: selectedRole, addPermissions, removePermissions },
      {
        onSuccess: () => {
          setShowConfirmModal(false);
          setIsPermissionsModalOpen(false);
          reloadUdsConfigurations();
        },
      },
    );
  };

  const handleDiscard = () =>
    setLocalPermissionIds(new Set(originalPermissionIds));

  const handleRoleChange = (role: AvailableRole) => {
    setSelectedRole(role);
    setLocalPermissionIds(new Set());
    setOriginalPermissionIds(new Set());
  };

  // Mobile: pick a role and immediately open its permissions sheet.
  const handleRoleOpen = (role: AvailableRole) => {
    handleRoleChange(role);
    setIsPermissionsModalOpen(true);
  };

  if (isAllPermissionsLoading)
    return <RolesPermissionsLoading label={t("LABEL_LOADING")} />;
  if (isPermissionsError)
    return <RolesPermissionsError label={t("ERROR_LOADING_PERMISSIONS")} />;

  const permissionsList = isRolePermissionsLoading ? (
    <RolesPermissionsLoading label={t("LABEL_LOADING")} />
  ) : (
    <ScrollView
      className="flex-1"
      contentContainerClassName="pb-4 gap-3"
      showsVerticalScrollIndicator={false}
    >
      {Object.entries(groupedByResourceGroup).map(
        ([resourceGroup, groupPermissions]) => {
          const groupTitle =
            resourceGroup?.charAt(0)?.toUpperCase() + resourceGroup?.slice(1);

          const allGroupEnabled = groupPermissions.every((permission) =>
            localPermissionIds.has(permission?.itemId),
          );

          return (
            <ScAccordion
              key={resourceGroup}
              title={groupTitle}
              defaultOpen={false}
              rightAction={
                <ScToggle
                  value={allGroupEnabled}
                  onChange={(value) =>
                    handleToggleGroup(groupPermissions, value)
                  }
                  disabled={isUpdating}
                />
              }
            >
              {groupPermissions?.map((permission) => (
                <PermissionItem
                  key={permission?.itemId}
                  label={permission?.name}
                  value={localPermissionIds.has(permission?.itemId)}
                  disabled={isUpdating}
                  onChange={(value) =>
                    handleTogglePermission(permission?.itemId, value)
                  }
                />
              ))}
            </ScAccordion>
          );
        },
      )}
    </ScrollView>
  );

  const actionButtons = (
    <View
      className={`flex-row gap-4 pt-4 ${
        isMobile ? "" : "border-t border-ash-3"
      }`}
    >
      <View className="flex-1">
        <ScButton
          text="BTN_DISCARD"
          mode="outlined"
          onPress={handleDiscard}
          disabled={!hasChanges || isUpdating}
        />
      </View>
      <View className="flex-1">
        <ScButton
          text="BTN_SAVE"
          mode="contained"
          onPress={handleSave}
          disabled={!hasChanges || isUpdating}
          loading={isUpdating}
        />
      </View>
    </View>
  );

  const confirmModal = (
    <ConfirmModal
      visible={showConfirmModal}
      title="QUEST_SAVE_PERMISSIONS"
      description="LABEL_SAVE_PERMISSIONS_DESC"
      confirmText="BTN_CONFIRM"
      cancelText="BTN_CANCEL"
      onCancel={() => setShowConfirmModal(false)}
      onConfirm={handleConfirmSave}
    />
  );

  // ── Mobile: borderless role rows; tapping one opens its permissions
  //    in a bottom sheet so there's no scrolling past the role list. ──
  if (isMobile) {
    return (
      <View className="gap-2">
        {AVAILABLE_ROLES.map((role) => (
          <RoleCard
            key={role}
            role={role}
            isMobile
            isSelected={selectedRole === role}
            permissionCount={rolePermissionCount}
            onPress={handleRoleOpen}
            adminDescription={t("LABEL_ADMIN_PERMISSION_DESC")}
            memberDescription={t("LABEL_MEMBER_PERMISSION_DESC")}
            permissionsLabel={t("LABEL_PERMISSIONS")}
          />
        ))}

        <Modal
          visible={isPermissionsModalOpen}
          transparent
          animationType="slide"
          onRequestClose={() => setIsPermissionsModalOpen(false)}
        >
          <View style={varStyle} className="flex-1 bg-shadow-dark justify-end">
            <View className="bg-chalk rounded-t-3xl h-[85%] px-4 pt-3 pb-3 gap-3">
              <View className="self-center w-10 h-1 rounded-full bg-ash-3" />
              <View className="flex-row items-center justify-between">
                <Text className="font-semibold text-onyx text-base capitalize">
                  {selectedRole} · {t("LABEL_PERMISSIONS")}
                </Text>
                <Pressable
                  className="p-1.5 rounded-full hover:bg-ash-3"
                  onPress={() => setIsPermissionsModalOpen(false)}
                  hitSlop={8}
                >
                  <CloseCircle
                    size={24}
                    color={themeVars["--color-primary-default"] as string}
                  />
                </Pressable>
              </View>

              {permissionsList}
              {actionButtons}
            </View>
          </View>
        </Modal>

        {confirmModal}
      </View>
    );
  }

  // ── Web/desktop: two-column roles + permissions ──
  return (
    <View className="sm:flex-row flex-col gap-6">
      <View
        className="bg-chalk border border-ash-3 p-4 rounded-[16px] gap-4"
        style={{ flex: 1 }}
      >
        <Text className="font-semibold text-onyx">{t("LABEL_ROLES")}</Text>
        {AVAILABLE_ROLES.map((role) => (
          <RoleCard
            key={role}
            role={role}
            isSelected={selectedRole === role}
            permissionCount={rolePermissionCount}
            onPress={handleRoleChange}
            adminDescription={t("LABEL_ADMIN_PERMISSION_DESC")}
            memberDescription={t("LABEL_MEMBER_PERMISSION_DESC")}
            permissionsLabel={t("LABEL_PERMISSIONS")}
          />
        ))}
      </View>

      {/* Permissions Column */}
      <View className="flex-1 bg-chalk border border-ash-3 p-4 rounded-[16px]">
        <Text className="font-semibold text-onyx mb-4">
          {t("LABEL_PERMISSIONS")}
        </Text>
        {permissionsList}
        {actionButtons}
      </View>

      {confirmModal}
    </View>
  );
};

export default RolesAndPermission;
